import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Modal, RefSelectProps, App } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import GraduationCapIcon from "../GraduationCapIcon";
import type { Dayjs } from "dayjs";
import dayjs from "../../../../utils/formacion/dayjs-timezone";
import {
  CreateTrainingPayload,
  SubjectCatalogItem,
  TrainingRow,
} from "../../../../services/formacion/formacion.types";
import {
  createTraining,
  getRegistrationImpact,
  getSubjectCatalog,
  updateTraining,
} from "../../../../services/formacion/formacion.service";
import { getApiErrorMessage } from "../../../../utils";
import { parseZoomWebinarId } from "../../../../utils/formacion/zoom-webinar-id";
import {
  ALL_SCHOOLS,
  SchoolOption,
  TARGET_SCHOOL_FIELD,
} from "../../../../utils/formacion/training-display";
import DetailsTab from "./DetailsTab";
import AudienceTab from "./AudienceTab";
import styles from "./styles.module.scss";

interface FormValues {
  title: string;
  startDate: Dayjs;
  startTime: Dayjs;
  duration_min: 45 | 60 | 90;
  registration_url: string;
  join_url: string;
  zoom_webinar_id?: string;
  timezone: string;
  cover_image_url?: string | null;
  audience_teachers: boolean;
  audience_coordinators: boolean;
  education_level: "KINDER" | "PRIMARIA";
  subjectIds: number[];
  /** Solo del formulario: decide la audiencia, no viaja tal cual en el payload. */
  target_school_id?: number[];
}

interface Props {
  open: boolean;
  /** El colegio que el LISTADO tiene filtrado: solo siembra el valor inicial. */
  schoolId: number | null;
  schoolName: string;
  schools?: SchoolOption[];
  training: TrainingRow | null;
  onClose: () => void;
  onSaved: () => void;
}

type TabKey = "details" | "audience";

const DETAILS_FIELDS = [
  "title",
  "startDate",
  "startTime",
  "duration_min",
  "registration_url",
  "join_url",
  "timezone",
];

const TrainingModal: React.FC<Props> = ({
  open,
  schoolId,
  schools = [],
  training,
  onClose,
  onSaved,
}) => {
  const { notification } = App.useApp();
  const [form] = Form.useForm<FormValues>();
  const [activeTab, setActiveTab] = useState<TabKey>("details");
  const [detailsConfirmed, setDetailsConfirmed] = useState(!!training);
  const [submitting, setSubmitting] = useState(false);
  const [subjectCatalog, setSubjectCatalog] = useState<SubjectCatalogItem[]>(
    []
  );
  const [subjectCatalogLoading, setSubjectCatalogLoading] = useState(true);
  const lastAutoFilledWebinarIdRef = useRef<string | null>(null);
  const schoolSelectRef = useRef<RefSelectProps>(null);
  // React 19 does not render the static `Modal.confirm`, so the SCRUM-1098
  // removal-impact confirmation has to come from the hook API with its
  // context holder mounted below (precedent: MoveToCampusModal/index.tsx).
  const [modal, modalContextHolder] = Modal.useModal();

  const isEditing = !!training;
  const trainingSchoolIds = training?.school_ids ?? [];

  useEffect(() => {
    setSubjectCatalogLoading(true);
    getSubjectCatalog()
      .then(setSubjectCatalog)
      .catch(() => {
        notification.error({
          message: "Error",
          description: "No se pudieron cargar las asignaturas.",
          placement: "top",
          duration: 4,
        });
      })
      .finally(() => setSubjectCatalogLoading(false));
  }, [notification]);

  useEffect(() => {
    setActiveTab("details");
    setDetailsConfirmed(!!training);
    lastAutoFilledWebinarIdRef.current = null;
    if (training) {
      const startsAt = dayjs(training.starts_at).tz(training.timezone);
      form.setFieldsValue({
        title: training.title,
        startDate: startsAt,
        startTime: startsAt,
        duration_min: training.duration_min,
        registration_url: training.registration_url,
        join_url: training.join_url,
        zoom_webinar_id:
          training.zoom_webinar_id != null
            ? String(training.zoom_webinar_id)
            : undefined,
        timezone: training.timezone,
        cover_image_url: training.cover_image_url ?? undefined,
        audience_teachers: training.audience_teachers,
        audience_coordinators: training.audience_coordinators,
        education_level: training.education_level,
        subjectIds: training.subjects.map((subject) => subject.id),
      });
      // Edit hydration (SCRUM-1098 design §6): `[]` on the wire means "all
      // schools"; `[ALL_SCHOOLS]` is how the UI represents that same state.
      form.setFieldsValue({
        [TARGET_SCHOOL_FIELD]:
          training.school_ids.length > 0
            ? training.school_ids
            : [ALL_SCHOOLS],
      });
    } else {
      form.resetFields();
      // Después del reset, nunca antes: la audiencia es una decisión de ESTA
      // capacitación, no del filtro con el que alguien miraba la tabla — el
      // filtro solo siembra el valor inicial.
      form.setFieldsValue({
        [TARGET_SCHOOL_FIELD]: schoolId !== null ? [schoolId] : [ALL_SCHOOLS],
      });
    }
  }, [training, form, schoolId]);

  const handleJoinUrlChange = (joinUrl: string) => {
    const parsed = parseZoomWebinarId(joinUrl);
    if (parsed === null) {
      return;
    }
    const parsedStr = String(parsed);
    const current = form.getFieldValue("zoom_webinar_id");
    if (!current || current === lastAutoFilledWebinarIdRef.current) {
      form.setFieldValue("zoom_webinar_id", parsedStr);
      lastAutoFilledWebinarIdRef.current = parsedStr;
    }
  };

  const readTargetSchoolIds = (): number[] =>
    form.getFieldValue(TARGET_SCHOOL_FIELD) ?? [ALL_SCHOOLS];

  const buildPayload = (): CreateTrainingPayload => {
    const values = form.getFieldsValue();
    const combinedLocal = `${values.startDate.format(
      "YYYY-MM-DD"
    )} ${values.startTime.format("HH:mm")}`;
    const startsAt = dayjs.tz(combinedLocal, values.timezone).utc().toISOString();

    return {
      title: values.title,
      starts_at: startsAt,
      duration_min: values.duration_min,
      timezone: values.timezone,
      registration_url: values.registration_url,
      join_url: values.join_url,
      zoom_webinar_id: values.zoom_webinar_id
        ? Number(values.zoom_webinar_id)
        : null,
      education_level: values.education_level,
      audience_teachers: !!values.audience_teachers,
      audience_coordinators: !!values.audience_coordinators,
      subjectIds: values.subjectIds ?? [],
      // SCRUM-1098 design §6: `[ALL_SCHOOLS]` on the wire is "all schools",
      // i.e. an empty (or omitted) schoolIds.
      schoolIds: readTargetSchoolIds().includes(ALL_SCHOOLS)
        ? []
        : readTargetSchoolIds(),
      ...(values.cover_image_url !== undefined && {
        cover_image_url: values.cover_image_url,
      }),
    };
  };

  // SCRUM-1098 design §3.4: the `?schoolId=` query param on create/update is
  // authorization/scoping, a different job from the body's `schoolIds`
  // audience — it keeps reporting a single school only when exactly one is
  // selected (ambiguous for several/all, so `null` there, same as before).
  const readTargetSchoolId = (): number | null => {
    const realIds = readTargetSchoolIds().filter((id) => id !== ALL_SCHOOLS);
    return realIds.length === 1 ? realIds[0] : null;
  };

  // SCRUM-1098 design §5/§6: removing a school already in `training.school_ids`
  // needs the registration-impact count before it can apply; `affected === 0`
  // skips the dialog entirely (design §5 — a "0 affected, confirm?" modal is
  // noise). Focus always returns to the Select, not the document body.
  const handleSchoolRemovalRequested = (
    schoolIdToRemove: number
  ): Promise<boolean> => {
    if (!training) return Promise.resolve(true);

    return getRegistrationImpact(training.id, [schoolIdToRemove])
      .then(
        ({ affected }) =>
          new Promise<boolean>((resolve) => {
            if (affected === 0) {
              resolve(true);
              return;
            }
            modal.confirm({
              title: "¿Quitar colegio de la audiencia?",
              content: (
                <div role="status">
                  {affected === 1
                    ? "1 persona de este colegio pierde acceso a la capacitación."
                    : `${affected} personas de este colegio pierden acceso a la capacitación.`}{" "}
                  Su registro en Zoom no se cancela.
                </div>
              ),
              okText: "Sí, quitar",
              cancelText: "No, mantener",
              afterClose: () => schoolSelectRef.current?.focus(),
              onOk: () => resolve(true),
              onCancel: () => resolve(false),
            });
          })
      )
      .catch((error) => {
        notification.error({
          message: "Error",
          description: getApiErrorMessage(
            error,
            "No se pudo calcular el impacto de quitar el colegio."
          ),
          placement: "top",
          duration: 4,
        });
        return false;
      });
  };

  const handleTabClick = (tab: TabKey) => {
    if (tab === "audience" && !isEditing && !detailsConfirmed) return;
    setActiveTab(tab);
  };

  const handlePrimaryAction = async () => {
    if (!isEditing && activeTab === "details") {
      try {
        await form.validateFields(DETAILS_FIELDS);
        setDetailsConfirmed(true);
        setActiveTab("audience");
      } catch {}
      return;
    }

    setSubmitting(true);
    try {
      await form.validateFields();
      const payload = buildPayload();
      if (isEditing && training) {
        await updateTraining(schoolId, training.id, payload);
      } else {
        await createTraining(readTargetSchoolId(), payload);
      }
      notification.success({
        message: "Éxito",
        description: isEditing
          ? "La capacitación fue actualizada exitosamente!"
          : "La capacitación fue publicada exitosamente!",
        placement: "top",
        duration: 3,
      });
      onSaved();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      notification.error({
        message: "Error",
        description: getApiErrorMessage(
          error,
          "La capacitación no pudo ser guardada."
        ),
        placement: "top",
        duration: 4,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const primaryLabel = !isEditing
    ? activeTab === "details"
      ? "Guardar"
      : "Publicar"
    : "Guardar";

  return (
    <>
      {modalContextHolder}
      <Modal
        open={open}
        onCancel={onClose}
        footer={null}
        width={560}
        closeIcon={<CloseOutlined className={styles.closeIcon} />}
        title={
          <div className={styles.header}>
            <GraduationCapIcon className={styles.headerIcon} />
            <span>
              {isEditing ? "Editar capacitación" : "Nueva capacitación"}
            </span>
          </div>
        }
      >
        <div className={styles.tabs}>
          <button
            type="button"
            className={`${styles.tab} ${
              activeTab === "details" ? styles.tabActive : ""
            }`}
            onClick={() => handleTabClick("details")}
          >
            Detalles
          </button>
          <button
            type="button"
            disabled={!isEditing && !detailsConfirmed}
            className={`${styles.tab} ${
              activeTab === "audience" ? styles.tabActive : ""
            } ${!isEditing && !detailsConfirmed ? styles.tabDisabled : ""}`}
            onClick={() => handleTabClick("audience")}
          >
            Audiencia
          </button>
        </div>

        <Form form={form} layout="vertical" preserve>
          <div
            style={{ display: activeTab === "details" ? "block" : "none" }}
          >
            <DetailsTab
              form={form}
              schoolId={schoolId}
              trainingId={training?.id}
              onJoinUrlChange={handleJoinUrlChange}
            />
          </div>
          <div
            style={{ display: activeTab === "audience" ? "block" : "none" }}
          >
            <AudienceTab
              form={form}
              schools={schools}
              isEditing={isEditing}
              trainingSchoolIds={trainingSchoolIds}
              onSchoolRemovalRequested={handleSchoolRemovalRequested}
              schoolSelectRef={schoolSelectRef}
              subjectCatalog={subjectCatalog}
              subjectCatalogLoading={subjectCatalogLoading}
            />
          </div>
        </Form>

        <div className={styles.footer}>
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            type="primary"
            className={styles.ctaButton}
            loading={submitting}
            onClick={handlePrimaryAction}
          >
            {primaryLabel}
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default TrainingModal;
